import clsx from 'clsx'
import React, { FunctionComponent, ReactElement, ReactNode, SyntheticEvent } from 'react'

import { ShellButton } from '../Button'
import { Icon } from '../Icon'
import { ShellLink } from '../Link'
import { Skeleton } from '../Skeleton'
import { TagComponent } from '../Tag'
import { RATIO } from './constants'
import styles from './style.module.scss'

type RatiosType = typeof RATIO
type RatiosKeys = keyof RatiosType
export type CardRatios = RatiosType[RatiosKeys]

export interface CardProps {
  href: string
  logo: ReactNode
  background: ReactNode
  ratio?: CardRatios
  favorited?: boolean
  onFavorited?: () => void
  favoritedButtonLabel?: string
  locationLink?: string
  locationLabel?: string
  tags?: Array<ReactElement<TagComponent>>
  testNameSpace?: string
  children: ReactNode
}

export type CardVariantComponent = FunctionComponent<CardProps>

export type CardComponent = CardVariantComponent & {
  Footer: FunctionComponent<CardFooterProp>
}

export const Card: CardComponent = ({
  children,
  href,
  logo,
  background,
  ratio = RATIO.RECTANGLE_3x4,
  favorited,
  favoritedButtonLabel = 'favorited',
  onFavorited,
  locationLink,
  locationLabel = 'in store',
  tags = [],
  testNameSpace = '',
}) => {
  const CardClassNames = clsx(styles.card)

  const hasTestId = testNameSpace.length > 0

  const handleOnFavorited = (e: SyntheticEvent): void => {
    if (typeof onFavorited === 'undefined') {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    onFavorited()
  }

  const renderActions = (): ReactNode => {
    if (typeof favorited === 'undefined' && typeof locationLink === 'undefined') {
      return
    }

    const favoritedButtonClasses = clsx(favorited && styles.favorited)

    return (
      <div className={styles.actions}>
        {typeof favorited !== 'undefined' && (
          <ShellButton className={favoritedButtonClasses} aria-label={favoritedButtonLabel} onClick={handleOnFavorited} testNameSpace={testNameSpace}>
            <Icon.Heart size='Small' />{' '}
          </ShellButton>
        )}
        {typeof locationLink !== 'undefined' && (
          <ShellButton aria-label={locationLabel} onClick={() => window.open(locationLink, '_blank')} testNameSpace={testNameSpace}>
            <Icon.Location size='Small' />
          </ShellButton>
        )}
      </div>
    )
  }

  const renderTags = (): ReactNode => {
    if (typeof tags === 'undefined' || tags.length === 0) {
      return
    }
    return <div className={styles.tags}>{tags}</div>
  }

  return (
    <div className={CardClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-card` })}>
      <ShellLink rel='noreferrer' target='_blank' href={href} testNameSpace={testNameSpace}>
        <div className={styles.content}>
          <Skeleton />
          <div className={clsx(styles.bg, styles[`bg${ratio}`])}>{background}</div>
          <div className={styles.info}>
            <div className={styles.logo}>{logo}</div>
            {renderActions()}
          </div>
          {renderTags()}
        </div>
      </ShellLink>
      {children}
    </div>
  )
}

interface CardFooterProp {
  children: ReactNode
}

const CardFooter: FunctionComponent<CardFooterProp> = ({ children }) => {
  return <div className={styles.footer}>{children ?? <Skeleton />}</div>
}

Card.Footer = CardFooter
